import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Grid,
  Avatar,
  Tooltip,
  Badge,
  CircularProgress,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperAdminIcon,
  ManageAccounts as ManagerIcon,
  Person as UserIcon,
  Visibility as ViewerIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useRoleContext } from '../contexts/RoleContext';

function UserManagement() {
  const { userRole, hasPermission } = useRoleContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [stats, setStats] = useState(null);

  // Check if user has super admin access
  if (!hasPermission('settings') || userRole !== 'super_admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Super Admin role required.
        </Alert>
      </Box>
    );
  }

  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/users/management', { // Fixed: Added v1
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        console.log('✅ Successfully loaded users');
      } else {
        setError('Failed to load users');
        console.error('❌ Load users failed:', response.status);
      }
    } catch (err) {
      setError('Error loading users');
      console.error('❌ Load users error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/users/stats', { // Fixed: Added v1
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        console.log('✅ Successfully loaded stats');
      } else {
        console.error('❌ Load stats failed:', response.status);
      }
    } catch (err) {
      console.error('❌ Load stats error:', err);
    }
  };

  const handleRoleUpdate = async () => {
    try {
      const token = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/users/${selectedUser.id}/role`, { // Fixed: Added v1
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        setEditDialog(false);
        setSelectedUser(null);
        setNewRole('');
        loadUsers();
        loadStats();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update role');
      }
    } catch (err) {
      setError('Error updating role');
      console.error(err);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/users/${userId}/status`, { // Fixed: Added v1
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isActive: !currentStatus }),
      });

      if (response.ok) {
        loadUsers();
        loadStats();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update status');
      }
    } catch (err) {
      setError('Error updating status');
      console.error(err);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <SuperAdminIcon />;
      case 'admin': return <AdminIcon />;
      case 'manager': return <ManagerIcon />;
      case 'user': return <UserIcon />;
      case 'viewer': return <ViewerIcon />;
      default: return <UserIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'warning';
      case 'manager': return 'info';
      case 'user': return 'success';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
    >
      <Box sx={{
        p: 4,
        flexShrink: 0,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      >
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            👥 User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage user roles and permissions across your organization
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Zoom in>
                <Card sx={{ background: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)' }}>
                  <CardContent sx={{ color: 'white', textAlign: 'center', py: 2 }}>
                    <PeopleIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.totalUsers}
                    </Typography>
                    <Typography variant="body2">Total Users</Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Zoom in style={{ transitionDelay: '100ms' }}>
                <Card sx={{ background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)' }}>
                  <CardContent sx={{ color: 'white', textAlign: 'center', py: 2 }}>
                    <ActiveIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.activeUsers}
                    </Typography>
                    <Typography variant="body2">Active Users</Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Zoom in style={{ transitionDelay: '200ms' }}>
                <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)' }}>
                  <CardContent sx={{ color: 'white', textAlign: 'center', py: 2 }}>
                    <AdminIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {(stats.roleBreakdown.admin || 0) + (stats.roleBreakdown.super_admin || 0)}
                    </Typography>
                    <Typography variant="body2">Administrators</Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Zoom in style={{ transitionDelay: '300ms' }}>
                <Card sx={{ background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)' }}>
                  <CardContent sx={{ color: 'white', textAlign: 'center', py: 2 }}>
                    <ScheduleIcon sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stats.recentUsers?.length || 0}
                    </Typography>
                    <Typography variant="body2">New This Week</Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Scrollable Users Table Container */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 4,
        pt: 2,
      }}
      >
        <Card sx={{ borderRadius: 3, height: 'fit-content' }}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
            }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                All Users (
                {users.length}
                )
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                sx={{ borderRadius: 2 }}
              >
                Invite User
              </Button>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                maxHeight: 'calc(100vh - 400px)', // Adjust based on header height
                overflow: 'auto',
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: 'grey.50',
                      fontWeight: 600,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    },
                  }}
                  >
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{
                            mr: 2, bgcolor: 'primary.main', width: 36, height: 36,
                          }}
                          >
                            {user.username[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user.username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email || user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getRoleIcon(user.role)}
                          label={user.role.replace('_', ' ').toUpperCase()}
                          color={getRoleColor(user.role)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.isActive ? <ActiveIcon /> : <BlockIcon />}
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.lastLogin
                            ? new Date(user.lastLogin).toLocaleDateString()
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Role">
                          <IconButton
                            onClick={() => {
                              setSelectedUser(user);
                              setNewRole(user.role);
                              setEditDialog(true);
                            }}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            onClick={() => handleStatusToggle(user.id, user.isActive)}
                            size="small"
                            color={user.isActive ? 'error' : 'success'}
                          >
                            {user.isActive ? <BlockIcon /> : <ActiveIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Edit Role Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            Update User Role
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Update role for
                {' '}
                <strong>{selectedUser.username}</strong>
              </Typography>
              <TextField
                select
                fullWidth
                label="New Role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                helperText="Choose the appropriate role for this user"
              >
                <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
                <MenuItem value="user">User - Basic access to forms and knowledge base</MenuItem>
                <MenuItem value="manager">Manager - Access to analytics and management</MenuItem>
                <MenuItem value="admin">Admin - Full system access except user management</MenuItem>
                <MenuItem value="super_admin">Super Admin - Full access including user management</MenuItem>
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRoleUpdate}
            variant="contained"
            disabled={!newRole || newRole === selectedUser?.role}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
